import { Alert, Card, Col, Input, Row, Timeline } from 'antd';
import { useQuery } from '@tanstack/react-query';
import GatewayAxiosClient from '../store/services/GatewayAxiosClient';
import { useSearchParams } from 'react-router-dom-v5-compat';
import Title from 'antd/lib/typography/Title';
import { format } from 'date-fns';
import type { DisplayTrackingStatus } from '../components/tracking/hooks/useTrackingEvents';
import type { TimeLineItemProps } from 'antd/lib/timeline/TimelineItem';

export function useTrackingPageData(carrierId: string, carrierTrackingId: string) {
  return useQuery({
    queryKey: ['trackingPageData', { carrierId, carrierTrackingId }],
    queryFn: async () => {
      const res = await GatewayAxiosClient.get(`/tracking-ui/api/v1/carrier/${carrierId}/${carrierTrackingId}`);
      return res.data;
    },
    enabled: !!carrierId && !!carrierTrackingId,
  });
}

const FORMAT = 'EEE MMMM do yyyy, h:mm a';

type TrackingViewProps = {
  latestEvent: DisplayTrackingStatus | undefined;
  displayEvents: DisplayTrackingStatus[];
  carrierTrackingId: string;
  carrierTrackingURL?: string;
};

const trackingLocationString = (ev: DisplayTrackingStatus) => {
  let locationString = '';
  if (ev.city) locationString += `${ev.city}, `;
  if (ev.region) locationString += `${ev.region}, `;
  if (ev.postalCode) locationString += `${ev.postalCode}, `;
  if (ev.country) locationString += `${ev.country}`;

  return locationString;
};

const TrackingTimeline = ({ events }: { events: DisplayTrackingStatus[] }) => {
  const formatTimelineProps = (timestamp: string | undefined) => {
    const props: Partial<TimeLineItemProps> = {};
    props.label = timestamp ? format(new Date(timestamp), FORMAT) : '';
    props.pending = timestamp ? false : true;
    props.color = timestamp ? 'green' : 'grey';
    return props;
  };

  return (
    <Timeline mode={'left'} style={{ marginTop: '10px' }}>
      {events.map((event, index: number) => (
        <Timeline.Item key={index} {...formatTimelineProps(event.eventDate ?? undefined)}>
          {event.description}
          {trackingLocationString(event) ? (
            <>
              <br />
              {trackingLocationString(event)}
            </>
          ) : null}
        </Timeline.Item>
      ))}
    </Timeline>
  );
};

const TrackingView = ({ latestEvent, displayEvents, carrierTrackingURL, carrierTrackingId }: TrackingViewProps) => {
  return carrierTrackingId ? (
    <Card
      headStyle={{ textAlign: 'center', fontWeight: 'bold', fontSize: '20px' }}
      title="Tracking Details"
      style={{ width: '100%' }}
    >
      <Row style={{ textAlign: 'center' }}>
        <Col span={24}>
          <div className="ant-card-head-title" style={{ fontSize: '18px', paddingTop: 0 }}>
            {latestEvent?.shipmentStatus || 'Processing'}
          </div>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <TrackingTimeline events={displayEvents} />
        </Col>
      </Row>
    </Card>
  ) : null;
};

export const TrackingStatus = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const carrierId = searchParams.get('carrierId');
  const carrierTrackingId = searchParams.get('trackingId');

  const { data, isLoading, isSuccess, isError } = useTrackingPageData(carrierId as string, carrierTrackingId as string);

  return (
    <div style={{ minWidth: '500px' }}>
      <Card style={{ marginBottom: '30px' }}>
        <Row>
          <Col xs={24} lg={6} style={{ marginRight: '30px', marginBottom: '30px' }}>
            <Title level={5}>Carrier</Title>
            <Input
              style={{ fontSize: '16px' }}
              defaultValue={carrierId || undefined}
              onBlur={(event) => {
                searchParams.set('carrierId', event.target.value);
                setSearchParams(searchParams);
              }}
            />
          </Col>
          <Col xs={24} lg={6} style={{ marginRight: '30px' }}>
            <Title level={5}>Tracking Id</Title>
            <Input
              style={{ fontSize: '16px' }}
              defaultValue={carrierTrackingId || undefined}
              onBlur={(event) => {
                searchParams.set('trackingId', event.target.value);
                setSearchParams(searchParams);
              }}
            />
          </Col>
        </Row>
      </Card>
      <Card loading={isLoading}>
        {isError && <Alert type="error" description={`Tracking info not found.`} />}
        {isSuccess && (
          <TrackingView
            latestEvent={data.trackingEvents[0]}
            displayEvents={data.trackingEvents.map((t: any) => {
              return { ...t, description: t.carrierDescription };
            })}
            carrierTrackingId={data.carrierTrackingId}
          />
        )}
      </Card>
    </div>
  );
};

export default TrackingStatus;
